import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import {
  PAEventType,
  ProductAnalyticsEvent
} from '@dadohr/shared/domainTypes/productAnalytics';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Popover, Typography } from '@material-ui/core';
import { compact, flatten } from 'lodash';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Color, ColoredChip } from '../../../../../../components/ColoredChip';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../../../layout/Flex';
import { filterTasksByStatus, getMessageStatuses } from '../../services/items';
import { getIndividualInstanceUrl } from '../../services/routes';
import { WithBreakpoints } from '../Breakpoints';
import { EmployeeAppCard } from '../EmployeeAppCard';
import { NAV_BG_COLOR } from './common';

export const NavContainer = styled('div')((p) => ({
  backgroundColor: NAV_BG_COLOR,
  paddingTop: p.theme.spacing(4),
  paddingBottom: p.theme.spacing(4),
  paddingLeft: p.theme.spacing(3),
  paddingRight: p.theme.spacing(3),
  height: '100%'
}));

export const NavItemContainer = styled('div')<{
  selected?: boolean;
}>((p) => ({
  color: p.selected ? undefined : p.theme.custom.colors.gray.w600,
  borderLeft: `3px solid ${
    p.selected ? p.theme.custom.colors.primary.main : 'transparent'
  }`,
  padding: p.theme.spacing(2),
  paddingLeft: p.theme.spacing(2),
  backgroundColor: 'white',
  width: '100%',
  ':hover': {
    backgroundColor: p.theme.custom.colors.gray.w100
  }
}));

const ItemChip = ({ color, count }: { color: Color; count: number }) => {
  return (
    <ColoredChip
      color={color}
      mode="light"
      width={count >= 100 ? 60 : count >= 10 ? 50 : 40}
    >
      {count}
    </ColoredChip>
  );
};

const Item = ({
  title,
  href,
  count,
  color,
  hideCount,
  selected,
  onClick
}: {
  title: React.ReactNode;
  href: string;
  count: number;
  color: Color;
  hideCount?: boolean;
  selected?: boolean;
  onClick?: () => void;
}) => {
  return (
    <Link to={href} style={{ width: '100%' }} onClick={onClick}>
      <NavItemContainer selected={selected}>
        <Typography variant="h6">
          <FlexContainer fullWidth>
            <FlexItem flex={1}>{title}</FlexItem>
            <FlexItem flex={0}>
              <FlexContainer justifyContent="end" fullWidth>
                {!hideCount && <ItemChip color={color} count={count} />}
                <ChevronRight size={18} />
              </FlexContainer>
            </FlexItem>
          </FlexContainer>
        </Typography>
      </NavItemContainer>
    </Link>
  );
};

export type InboxNavGroupKey =
  | 'pinned'
  | 'inProgress'
  | 'forLater'
  | 'archive'
  | 'moments';
export type InboxNavItemKey =
  | 'everything'
  | 'pinned-tasks'
  | 'overdue'
  | 'todo'
  | 'msgs'
  | 'upcoming'
  | 'upcoming-msgs'
  | 'complete'
  | 'optional'
  | 'archived-msgs'
  | 'moments-active'
  | 'moments-archived';

export type NavBarItem<K> = {
  key: K;
  title: React.ReactNode;
  count: number;
  color: Color;
  selected?: boolean;
  hideCount?: boolean;
  href: string;
  trackingKey: string;
};

export type NavBarGroup<K, I> = {
  key: K;
  title?: React.ReactNode;
  collapsible?: boolean;
  items: NavBarItem<I>[];
};

export const NavBar = <K extends string, L extends string>({
  groups,
  onNavItemClick,
  topActions
}: {
  groups: NavBarGroup<K, L>[];
  onNavItemClick: (item: NavBarItem<L>) => void;
  topActions?: React.ReactNode;
}) => {
  return (
    <NavContainer>
      <FlexContainerVertical spacing={4} fullWidth>
        {topActions}
        {groups.map((g) => {
          const hasAnySelected = !!g.items.find((x) => x.selected);
          return (
            <FlexContainerVertical spacing={2} fullWidth>
              {g.title && (
                <Link
                  to={g.items[0]?.href}
                  style={{ width: '100%' }}
                  onClick={() => onNavItemClick(g.items[0])}
                >
                  <Typography variant="h5">
                    {g.collapsible ? (
                      <FlexContainer fullWidth>
                        <div>{g.title}</div>
                        {hasAnySelected ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronRight size={20} />
                        )}
                      </FlexContainer>
                    ) : (
                      <div>{g.title}</div>
                    )}
                  </Typography>
                </Link>
              )}
              {(!g.collapsible || hasAnySelected) && (
                <FlexContainerVertical spacing={0}>
                  {g.items.map((item) => (
                    <Item {...item} onClick={() => onNavItemClick(item)} />
                  ))}
                </FlexContainerVertical>
              )}
            </FlexContainerVertical>
          );
        })}
      </FlexContainerVertical>
    </NavContainer>
  );
};

const SHOW_UPCOMING_MESSAGES = false;

const getNavGroups = (
  actorAccessToken: string,
  instanceAccessToken: string,
  instance: Doc<IExperienceDenormalizedV2>,
  t: UnsafeTFunction,
  style: 'bar' | 'dropdown'
): NavBarGroup<InboxNavGroupKey, InboxNavItemKey>[] => {
  const getTasksWithStatus = (statuses: TaskStatus[]) =>
    filterTasksByStatus(instance, statuses);
  const todo = getTasksWithStatus([TaskStatus.RUNNING]);
  const upcoming = getTasksWithStatus([TaskStatus.NOT_STARTED]);
  const late = getTasksWithStatus([TaskStatus.LATE]);
  const optional = getTasksWithStatus([TaskStatus.PAST_DEADLINE]);
  const complete = getTasksWithStatus([
    TaskStatus.COMPLETE,
    TaskStatus.SKIPPED
  ]);

  const msgsStatus = getMessageStatuses(instance);
  const msgs = instance.data.messages;
  const msgsCurrent = msgs.filter(
    (m) => msgsStatus[m.templateId] === 'CURRENT'
  );

  const msgsForLater = msgs.filter((m) => msgsStatus[m.templateId] === 'LATER');
  const msgsArchived = msgs.filter(
    (m) => msgsStatus[m.templateId] === 'ARCHIVED'
  );

  const withHrefFromKey = (
    partialItem: Omit<NavBarItem<InboxNavItemKey>, 'href'>
  ): NavBarItem<InboxNavItemKey> => ({
    ...partialItem,
    href: getIndividualInstanceUrl(actorAccessToken, instanceAccessToken, {
      g: partialItem.key
    })
  });

  return [
    {
      key: 'inProgress',
      title: t('In progress'),
      items: compact<Omit<NavBarItem<InboxNavItemKey>, 'href'>>([
        {
          key: 'everything',
          title:
            style === 'bar' ? t('Everything') : t('Everything in progress'),
          color: 'gray',
          count: todo.length + late.length + msgsCurrent.length,
          hideCount: style === 'bar',
          trackingKey: 'Everything'
        },
        late.length && {
          key: 'overdue',
          title: t('Overdue tasks'),
          color: 'red',
          count: late.length,
          trackingKey: 'Overdue tasks'
        },
        {
          key: 'todo',
          title: t('To do tasks'),
          color: 'blue',
          count: todo.length,
          selected: true,
          trackingKey: 'To do tasks'
        },
        {
          key: 'msgs',
          title: t('Messages'),
          color: 'gray',
          count: msgsCurrent.length,
          selected: false,
          trackingKey: 'Messages'
        }
      ]).map(withHrefFromKey)
    },
    {
      key: 'forLater',
      title: t('For later'),
      collapsible: true,
      items: compact([
        {
          key: 'upcoming' as const,
          title: t('Upcoming tasks'),
          color: 'gray' as const,
          count: upcoming.length,
          trackingKey: 'Upcoming tasks'
        },
        SHOW_UPCOMING_MESSAGES && {
          key: 'upcoming-msgs' as const,
          title: t('Upcoming messages'),
          color: 'gray' as const,
          count: msgsForLater.length,
          selected: false,
          trackingKey: 'Upcoming Messages'
        }
      ]).map(withHrefFromKey)
    },
    {
      key: 'archive',
      title: t('Archived'),
      items: compact<Omit<NavBarItem<InboxNavItemKey>, 'href'>>([
        {
          key: 'complete',
          title: t('Completed tasks'),
          color: 'green',
          count: complete.length,
          selected: false,
          trackingKey: 'Completed tasks'
        },
        optional.length && {
          key: 'optional',
          title: t('Optional tasks'),
          color: 'gray',
          count: optional.length,
          selected: false,
          trackingKey: 'Optional tasks'
        },
        {
          key: 'archived-msgs',
          title: t('Messages'),
          color: 'gray',
          count: msgsArchived.length,
          selected: false,
          trackingKey: 'Archived messages'
        }
      ]).map(withHrefFromKey)
    }
  ];
};

export const withMarkedSelection = <K extends string, L extends string>(
  gs: NavBarGroup<K, L>[],
  g: string
): NavBarGroup<K, L>[] => {
  return gs.map((gr) => {
    return {
      ...gr,
      items: gr.items.map((item) => ({ ...item, selected: item.key === g }))
    };
  });
};

const useNavGroups = (
  actorAccessToken: string,
  instanceAccessToken: string,
  instance: Doc<IExperienceDenormalizedV2>,
  g: string,
  style: 'bar' | 'dropdown'
) => {
  const { t } = useUnsafeTranslation();
  return useMemo<NavBarGroup<InboxNavGroupKey, InboxNavItemKey>[]>(() => {
    const groups = getNavGroups(
      actorAccessToken,
      instanceAccessToken,
      instance,
      t,
      style
    );
    return withMarkedSelection(groups, g);
  }, [instance, t, instanceAccessToken, actorAccessToken, g, style]);
};

export const InboxStyleNavBar = ({
  actorAccessToken,
  instanceAccessToken,
  instance,
  g,
  onNavItemClick
}: {
  actorAccessToken: string;
  instanceAccessToken: string;
  instance: Doc<IExperienceDenormalizedV2>;
  g: InboxNavItemKey;
  onNavItemClick: (item: NavBarItem<InboxNavItemKey>) => void;
}) => {
  const groups = useNavGroups(
    actorAccessToken,
    instanceAccessToken,
    instance,
    g,
    'bar'
  );
  return <NavBar groups={groups} onNavItemClick={onNavItemClick} />;
};

const NavDropdownGroupHeader = ({ g }: { g: NavBarGroup<any, any> }) => {
  return <Typography variant="h6">{g.title}</Typography>;
};

const NavDropDownItem = ({ item }: { item: NavBarItem<any> }) => {
  return (
    <FlexContainer fullWidth>
      <ItemChip color={item.color} count={item.count} />
      <div>{item.title}</div>
    </FlexContainer>
  );
};

export const NavDropdown = <K extends string, L extends string>({
  groups,
  onNavItemClick,
  topActions
}: {
  groups: NavBarGroup<K, L>[];
  onNavItemClick: (item: NavBarItem<L>) => void;
  topActions?: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => {
    const items = flatten(groups.map((g) => g.items));
    return items.find((x) => x.selected) || items[0];
  }, [groups]);

  // TODO add filter actions

  return (
    <FlexContainerVertical fullWidth>
      {topActions}
      <div ref={ref} style={{ width: '100%' }}>
        <EmployeeAppCard onClick={() => setOpen(true)}>
          <Typography variant="h6">
            <FlexContainer fullWidth>
              <NavDropDownItem item={selected} />
              <ChevronDown size={20} />
            </FlexContainer>
          </Typography>
        </EmployeeAppCard>
        <Popover
          anchorEl={ref.current}
          open={open}
          onClose={() => setOpen(false)}
          classes={{
            paper: css({ borderRadius: `${theme.spacing(2)}px !important` })
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            style: {
              width: `calc(100% - ${theme.spacing(8)}px)`
            }
          }}
        >
          <FlexContainerVertical fullWidth spacing={0}>
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                <Link
                  style={{ width: '100%' }}
                  to={g.items[0]?.href}
                  onClick={() => onNavItemClick(g.items[0])}
                >
                  <EmployeeAppCard
                    noRoundBorders
                    onClick={() => setOpen(false)}
                  >
                    <NavDropdownGroupHeader g={g} />
                  </EmployeeAppCard>
                </Link>
                {g.items.map((item) => (
                  <Link
                    style={{ width: '100%' }}
                    to={item.href}
                    key={item.key}
                    onClick={() => onNavItemClick(item)}
                  >
                    <EmployeeAppCard
                      onClick={() => setOpen(false)}
                      highlight={item.selected}
                      hoverEffect
                      noRoundBorders
                    >
                      <NavDropDownItem item={item} />
                    </EmployeeAppCard>
                  </Link>
                ))}
              </React.Fragment>
            ))}
          </FlexContainerVertical>
        </Popover>
      </div>
    </FlexContainerVertical>
  );
};

export const InboxStyleNavDropdown = ({
  actorAccessToken,
  instanceAccessToken,
  instance,
  g,
  onNavItemClick
}: {
  actorAccessToken: string;
  instanceAccessToken: string;
  instance: Doc<IExperienceDenormalizedV2>;
  g: InboxNavItemKey;
  onNavItemClick: (item: NavBarItem<InboxNavItemKey>) => void;
}) => {
  const groups = useNavGroups(
    actorAccessToken,
    instanceAccessToken,
    instance,
    g,
    'dropdown'
  );
  return <NavDropdown groups={groups} onNavItemClick={onNavItemClick} />;
};

export const InboxStyleNav = ({
  trackFn,
  ...props
}: {
  actorAccessToken: string;
  instanceAccessToken: string;
  instance: Doc<IExperienceDenormalizedV2>;
  g: InboxNavItemKey;
  trackFn: (ev: ProductAnalyticsEvent) => void;
}) => {
  const onNavItemClick = (item: NavBarItem<InboxNavItemKey> | undefined) => {
    item &&
      trackFn({
        eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
        name: 'Status Navigation Clicked',
        Explanation: 'User clicked on a section in the status navigation',
        Status: item.trackingKey,
        'Item Count': item.count
      });
  };
  return (
    <WithBreakpoints
      lgMd={<InboxStyleNavBar {...props} onNavItemClick={onNavItemClick} />}
      smXs={
        <InboxStyleNavDropdown {...props} onNavItemClick={onNavItemClick} />
      }
    />
  );
};
